import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Typography, Paper, FormControlLabel, Checkbox, Autocomplete, TextField, CircularProgress, Chip, Alert, Dialog, DialogContent, DialogTitle, DialogActions, Button, List, ListItem, ListItemText, IconButton } from '@mui/material';
import { useJsApiLoader, GoogleMap } from '@react-google-maps/api';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import StraightenIcon from '@mui/icons-material/Straighten';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import BuildIcon from '@mui/icons-material/Build';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import CloseIcon from '@mui/icons-material/Close';
import { apiRequest } from '../utils/api';
import type { User, Job } from '../types';
import JobDetail from './job/JobDetail';

const libraries: ('places')[] = ['places'];

interface CurrentPosition {
  id: number;
  userId: number | null;
  latitude: number | null;
  longitude: number | null;
  userName?: string | null;
  updatedAt: string;
}

interface MapMarker {
  id: string;
  type: 'user' | 'job';
  position: google.maps.LatLngLiteral;
  title: string;
  description: string;
  data: CurrentPosition | Job | Array<{
    id: number;
    userName: string;
    timeAgo: string;
    data: CurrentPosition;
  }>;
}

const defaultCenter = {
  lat: 40.7128, // New York City
  lng: -74.0060,
};

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

// Helper function to create Material-UI icon marker SVG URL
const createMaterialIconMarker = (iconPath: string, color: string, size: 'small' | 'large' = 'small') => {
  const width = size === 'large' ? 40 : 32;
  const height = size === 'large' ? 50 : 40;
  const centerX = width / 2;
  const iconSize = size === 'large' ? 28 : 24;
  const iconOffset = (width - iconSize) / 2;
  
  return 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
      <path d="M${centerX} 0C${centerX * 0.448} 0 0 ${centerX * 0.448} 0 ${centerX}c0 ${centerX * 0.552} ${centerX} ${centerX * 1.5} ${centerX} ${centerX * 1.5}s${centerX}-${centerX * 0.948} ${centerX}-${centerX * 1.5}C${width} ${centerX * 0.448} ${width - centerX * 0.552} 0 ${centerX} 0z" fill="${color}"/>
      <g transform="translate(${iconOffset}, ${iconOffset})">
        <svg viewBox="0 0 24 24" width="${iconSize}" height="${iconSize}">
          ${iconPath}
        </svg>
      </g>
    </svg>
  `);
};

// Worker icon marker (user icon)
const workerIconPath = '<path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="white"/>';
const workerIconUrl = createMaterialIconMarker(workerIconPath, '#4285F4');

// Material-UI icon paths for each job status (24x24 viewBox paths)
const jobStatusIconConfig: Record<string, { iconPath: string; color: string; IconComponent: React.ComponentType<any> }> = {
  'Estimate': {
    iconPath: '<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" fill="white"/>',
    color: '#FF9800',
    IconComponent: DescriptionIcon,
  },
  'Proposal': {
    iconPath: '<path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="white"/>',
    color: '#2196F3',
    IconComponent: SendIcon,
  },
  'Measure': {
    iconPath: '<path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2m0 10H3V8h2v4h2V8h2v4h2V8h2v4h2V8h2v4h2V8h2z" fill="white"/>',
    color: '#9C27B0',
    IconComponent: StraightenIcon,
  },
  'Shop Drawing': {
    iconPath: '<path d="M6.36 18.78 6.61 21l1.62-1.54 2.77-7.6c-.68-.17-1.28-.51-1.77-.98zm8.41-7.9c-.49.47-1.1.81-1.77.98l2.77 7.6L17.39 21l.26-2.22zM15 8c0-1.3-.84-2.4-2-2.82V3h-2v2.18C9.84 5.6 9 6.7 9 8c0 1.66 1.34 3 3 3s3-1.34 3-3m-3 1c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1" fill="white"/>',
    color: '#00BCD4',
    IconComponent: ArchitectureIcon,
  },
  'Fabrication': {
    iconPath: '<path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z" fill="white"/>',
    color: '#FF5722',
    IconComponent: BuildIcon,
  },
  'Install': {
    iconPath: '<path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="white"/>',
    color: '#4CAF50',
    IconComponent: SettingsIcon,
  },
  'Finish': {
    iconPath: '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" fill="white"/>',
    color: '#795548',
    IconComponent: CheckCircleIcon,
  },
};

// Icon URL configuration for job statuses (for map markers) - larger size
const jobStatusIconUrls: Record<string, string> = {
  'Estimate': createMaterialIconMarker(jobStatusIconConfig['Estimate'].iconPath, jobStatusIconConfig['Estimate'].color, 'large'),
  'Proposal': createMaterialIconMarker(jobStatusIconConfig['Proposal'].iconPath, jobStatusIconConfig['Proposal'].color, 'large'),
  'Measure': createMaterialIconMarker(jobStatusIconConfig['Measure'].iconPath, jobStatusIconConfig['Measure'].color, 'large'),
  'Shop Drawing': createMaterialIconMarker(jobStatusIconConfig['Shop Drawing'].iconPath, jobStatusIconConfig['Shop Drawing'].color, 'large'),
  'Fabrication': createMaterialIconMarker(jobStatusIconConfig['Fabrication'].iconPath, jobStatusIconConfig['Fabrication'].color, 'large'),
  'Install': createMaterialIconMarker(jobStatusIconConfig['Install'].iconPath, jobStatusIconConfig['Install'].color, 'large'),
  'Finish': createMaterialIconMarker(jobStatusIconConfig['Finish'].iconPath, jobStatusIconConfig['Finish'].color, 'large'),
};

// Helper function to get icon for job status (for map markers) - larger size
const getJobStatusIcon = (jobStatus: string | null | undefined): google.maps.Icon | undefined => {
  if (!window.google?.maps) return undefined;
  
  const status = jobStatus || 'Estimate';
  const iconUrl = jobStatusIconUrls[status] || jobStatusIconUrls['Estimate'];
  
  return {
    url: iconUrl,
    scaledSize: new window.google.maps.Size(40, 50), // Larger than worker markers (32x40)
    anchor: new window.google.maps.Point(20, 50),
  };
};

export default function MapPage() {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<globalThis.Map<string, google.maps.Marker>>(new globalThis.Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const [selectedMarker, setSelectedMarker] = useState<MapMarker | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [currentPositions, setCurrentPositions] = useState<CurrentPosition[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  // Filter states
  const [showWorkers, setShowWorkers] = useState(true);
  const [showJobs, setShowJobs] = useState(true);
  const [selectedJobStatuses, setSelectedJobStatuses] = useState<string[]>([]);
  
  // Job detail modal state
  const [jobModalOpen, setJobModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  
  // Worker status list modal state
  const [statusListModalOpen, setStatusListModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<'active' | 'inactive' | 'absent' | null>(null);
  
  // Geocoding cache for jobs
  const jobCoordinatesCache = useRef<globalThis.Map<number, google.maps.LatLngLiteral>>(new globalThis.Map());
  
  // Company location
  const companyAddress = '2126 Flatbush Ave, Brooklyn, NY 11234';
  const companyCoordinatesRef = useRef<google.maps.LatLngLiteral | null>(null);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    libraries,
  });

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch current positions, jobs, and users
        // Only fetch active jobs
        const [positionsRes, jobsRes, usersRes] = await Promise.all([
          apiRequest('/current-positions', { method: 'GET' }),
          apiRequest('/jobs?showActive=true', { method: 'GET' }),
          apiRequest('/users', { method: 'GET' }),
        ]);

        setCurrentPositions(Array.isArray(positionsRes.data) ? positionsRes.data : []);
        // Jobs API returns { data: [...], pagination: {...} }
        const jobsData = jobsRes.data?.data || jobsRes.data;
        // Filter to ensure only active jobs are shown (double-check client-side)
        const activeJobs = Array.isArray(jobsData) 
          ? jobsData.filter((job: any) => job.active !== false)
          : [];
        setJobs(activeJobs);
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
        setDataReady(true);
      } catch (error) {
        console.error('Error fetching map data:', error);
        // Ensure arrays are set even on error
        setCurrentPositions([]);
        setJobs([]);
        setUsers([]);
        setDataReady(true); // Still mark as ready even on error so UI can render
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Geocode job addresses
  const geocodeJob = async (job: any): Promise<google.maps.LatLngLiteral | null> => {
    // Check cache first
    if (jobCoordinatesCache.current.has(job.id)) {
      return jobCoordinatesCache.current.get(job.id)!;
    }

    const jobAddress = job.job_address || job.jobAddress;
    if (!jobAddress || !isLoaded || !window.google?.maps) return null;

    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve) => {
        geocoder.geocode({ address: jobAddress }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            const lat = typeof location.lat === 'function' ? (location.lat as unknown as () => number)() : (location.lat as unknown as number);
            const lng = typeof location.lng === 'function' ? (location.lng as unknown as () => number)() : (location.lng as unknown as number);
            const coords: google.maps.LatLngLiteral = { lat, lng };
            jobCoordinatesCache.current.set(job.id, coords);
            resolve(coords);
          } else {
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error geocoding job:', error);
      return null;
    }
  };

  // Geocode company address
  const geocodeCompanyAddress = async (): Promise<google.maps.LatLngLiteral | null> => {
    if (companyCoordinatesRef.current) {
      return companyCoordinatesRef.current;
    }

    if (!isLoaded || !window.google?.maps) return null;

    try {
      const geocoder = new window.google.maps.Geocoder();
      return new Promise((resolve) => {
        geocoder.geocode({ address: companyAddress }, (results, status) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            const lat = typeof location.lat === 'function' ? (location.lat as unknown as () => number)() : (location.lat as unknown as number);
            const lng = typeof location.lng === 'function' ? (location.lng as unknown as () => number)() : (location.lng as unknown as number);
            const coords: google.maps.LatLngLiteral = { lat, lng };
            companyCoordinatesRef.current = coords;
            resolve(coords);
          } else {
            console.error('Error geocoding company address:', status);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error geocoding company address:', error);
      return null;
    }
  };

  // Helper function to determine worker status based on updatedAt from currentPositions
  // Returns: 'active' (<2h), 'inactive' (2-16h), 'absent' (>16h or no update), or null if no status should be displayed
  // All time calculations are done in UTC to ensure consistency across timezones
  // Wrapped in useCallback to ensure stable reference for useMemo dependency
  const getWorkerStatus = useCallback((updatedAt: string | null | undefined): 'active' | 'inactive' | 'absent' | null => {
    if (!updatedAt) return 'absent'; // No position update
    try {
      // Parse the updatedAt timestamp (ISO string from backend, always UTC with 'Z' suffix)
      // new Date() correctly parses ISO strings with timezone info
      const updateDate = new Date(updatedAt);
      
      // Validate the date
      if (isNaN(updateDate.getTime())) {
        return 'absent';
      }
      
      // Get current time in UTC (Date.now() returns UTC milliseconds since epoch)
      // getTime() also returns UTC milliseconds since epoch
      // Both are in the same timezone reference (UTC), so the difference is accurate
      const currentTimeUTC = Date.now();
      const updateTimeUTC = updateDate.getTime();
      
      // Calculate hours difference (both times are in UTC milliseconds)
      const hoursSinceUpdate = (currentTimeUTC - updateTimeUTC) / (1000 * 60 * 60);
      
      // Handle edge case where update time is in the future (shouldn't happen, but handle gracefully)
      if (hoursSinceUpdate < 0) {
        return 'active'; // If somehow in future, treat as active
      }
      
      if (hoursSinceUpdate < 2) {
        return 'active';
      } else if (hoursSinceUpdate < 16) {
        return 'inactive';
      } else {
        return 'absent'; // More than 16 hours - hasn't clocked in
      }
    } catch {
      return 'absent';
    }
  }, []);

  // Update markers on map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || loading) return;

    const map = mapRef.current;
    
    // Clear existing markers and close InfoWindow
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current.clear();

    const markers: MapMarker[] = [];

    // Group user locations by coordinates (same location)
    interface GroupedUser {
      position: google.maps.LatLngLiteral;
      users: Array<{
        id: number;
        userName: string;
        timeAgo: string;
        status: 'active' | 'inactive' | 'absent' | null;
        data: CurrentPosition;
      }>;
    }

    const userGroups = new Map<string, GroupedUser>();

    // Add user locations (current positions) - group by location
    if (showWorkers) {
          currentPositions
        .filter((pos) => {
          // Filter: must have valid coordinates and userId
          if (pos.latitude === null || pos.longitude === null || !pos.userId) {
            return false;
          }
          // Filter: only show approved users
          const user = Array.isArray(users) ? users.find((u) => u.id === pos.userId) : null;
          if (!user || !user.approved) {
            return false; // Skip pending/non-approved users
          }
          // Filter: only show users who have clocked in (not absent)
          const status = getWorkerStatus(pos.updatedAt);
          if (status === 'absent') {
            return false; // Skip absent workers
          }
          return true;
        })
        .forEach((pos) => {
          const user = Array.isArray(users) ? users.find((u) => u.id === pos.userId) : null;
          const userName = pos.userName || user?.username || `User ${pos.userId}`;
          const timeAgo = pos.updatedAt ? new Date(pos.updatedAt).toLocaleString() : 'Unknown';
          
          // Get worker status based on updatedAt from currentPositions (user is already confirmed as approved)
          const status = getWorkerStatus(pos.updatedAt);
          
          // Create a key for grouping (round to 6 decimal places to group nearby locations)
          const lat = Math.round(pos.latitude! * 1000000) / 1000000;
          const lng = Math.round(pos.longitude! * 1000000) / 1000000;
          const locationKey = `${lat},${lng}`;
          
          if (!userGroups.has(locationKey)) {
            userGroups.set(locationKey, {
              position: { lat, lng },
              users: [],
            });
          }
          
          userGroups.get(locationKey)!.users.push({
            id: pos.id,
            userName,
            timeAgo,
            status,
            data: pos,
          });
        });

      // Create markers for grouped users
      userGroups.forEach((group, locationKey) => {
        const userCount = group.users.length;
        const firstUser = group.users[0];
        
        markers.push({
          id: `user-group-${locationKey}`,
          type: 'user',
          position: group.position,
          title: userCount === 1 ? firstUser.userName : `${userCount} Workers`,
          description: userCount === 1 
            ? `Last updated: ${firstUser.timeAgo}`
            : `Multiple workers at this location`,
          data: group.users, // Store all users for this location
        });
      });
    }

    // Add markers to map
    markers.forEach((markerData) => {
      let markerIcon: google.maps.Icon | undefined = undefined;
      
      if (markerData.type === 'user') {
        const userGroup = markerData.data as GroupedUser['users'];
        const userCount = Array.isArray(userGroup) ? userGroup.length : 1;
        
        // Create icon with count badge if multiple users
        if (userCount > 1) {
          // Create badge icon using SVG
          const badgeSvg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
              <path d="M16 0C7.163 0 0 7.163 0 16c0 8.837 16 24 16 24s16-15.163 16-24C32 7.163 24.837 0 16 0z" fill="#4285F4"/>
              <g transform="translate(4, 4)">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="white"/>
                </svg>
              </g>
              <circle cx="24" cy="8" r="8" fill="#FF5722" stroke="#ffffff" stroke-width="1.5"/>
              <text x="24" y="11" font-family="Arial" font-size="9" font-weight="bold" fill="white" text-anchor="middle">${userCount > 9 ? '9+' : userCount}</text>
            </svg>
          `;
          
          markerIcon = {
            url: 'data:image/svg+xml;base64,' + btoa(badgeSvg),
            scaledSize: new window.google.maps.Size(32, 40),
            anchor: new window.google.maps.Point(16, 40),
          };
        } else {
          markerIcon = {
            url: workerIconUrl,
            scaledSize: new window.google.maps.Size(32, 40),
            anchor: new window.google.maps.Point(16, 40),
          };
        }
      }
      
      const marker = new window.google.maps.Marker({
        position: markerData.position,
        map: map,
        title: markerData.title,
        icon: markerIcon,
        zIndex: markerData.type === 'user' ? 2000 : 1500, // Workers on top, then jobs
      });

      const showInfoWindow = () => {
        // Close any existing InfoWindow
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
        
        // Create and open new InfoWindow
        if (map && window.google?.maps) {
          let content = '';
          
          if (markerData.type === 'user' && Array.isArray(markerData.data)) {
            // Multiple users at this location
            const userGroup = markerData.data as GroupedUser['users'];
            content = `
              <div style="min-width: 200px; padding: 8px;">
                <strong style="font-size: 14px; margin-bottom: 8px; display: block;">Workers at this location (${userGroup.length}):</strong>
                ${userGroup.map((user) => {
                  let statusColor = '#9E9E9E';
                  let statusText = 'Inactive';
                  if (user.status === 'active') {
                    statusColor = '#4CAF50';
                    statusText = 'Active';
                  } else if (user.status === 'inactive') {
                    statusColor = '#FF9800';
                    statusText = 'Inactive';
                  }
                  // Only show status if it's not null (i.e., user is approved and has status)
                  const statusBadge = user.status !== null 
                    ? `<span style="background-color: ${statusColor}; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold;">${statusText}</span>`
                    : '';
                  return `
                  <div style="padding: 4px 0; border-bottom: 1px solid #e0e0e0;">
                    <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px;">
                      <strong>${user.userName}</strong>
                      ${statusBadge}
                    </div>
                    <small style="color: #666;">Last updated: ${user.timeAgo}</small>
                  </div>
                `;
                }).join('')}
              </div>
            `;
          } else if (markerData.type === 'user') {
            // Single user
            const userData = markerData.data as CurrentPosition;
            const user = Array.isArray(users) ? users.find((u) => u.id === userData.userId) : null;
            const status = user?.approved ? getWorkerStatus(userData.updatedAt) : null;
            let statusColor = '#9E9E9E';
            let statusText = 'Inactive';
            if (status === 'active') {
              statusColor = '#4CAF50';
              statusText = 'Active';
            } else if (status === 'inactive') {
              statusColor = '#FF9800';
              statusText = 'Inactive';
            }
            // Only show status if it's not null
            const statusBadge = status !== null 
              ? `<span style="background-color: ${statusColor}; color: white; padding: 2px 6px; border-radius: 3px; font-size: 10px; font-weight: bold;">${statusText}</span>`
              : '';
            content = `
              <div style="min-width: 200px; padding: 8px;">
                <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                  <strong>${markerData.title}</strong>
                  ${statusBadge}
                </div>
                <small style="color: #666;">${markerData.description}</small>
              </div>
            `;
          } else {
            // Job
            content = `
              <div style="min-width: 200px; padding: 8px;">
                <strong>${markerData.title}</strong><br/>
                <small>${markerData.description}</small>
              </div>
            `;
          }
          
          const infoWindow = new window.google.maps.InfoWindow({
            content,
          });
          
          infoWindow.open({
            anchor: marker,
            map: map,
          });
          
          infoWindowRef.current = infoWindow;
        }
        
        setSelectedMarker(markerData);
      };

      // Show InfoWindow on hover
      marker.addListener('mouseover', showInfoWindow);
      
      // Also show on click
      marker.addListener('click', showInfoWindow);
      
      // Close InfoWindow on mouseout
      marker.addListener('mouseout', () => {
        if (infoWindowRef.current) {
          infoWindowRef.current.close();
          infoWindowRef.current = null;
        }
        setSelectedMarker(null);
      });

      markersRef.current.set(markerData.id, marker);
    });

    // Add company location marker
    geocodeCompanyAddress().then((coords) => {
      if (coords && map && window.google?.maps) {
        // Create large red marker for company location
        const companyMarkerSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" viewBox="0 0 48 60">
            <path d="M24 0C10.745 0 0 10.745 0 24c0 13.255 24 36 24 36s24-22.745 24-36C48 10.745 37.255 0 24 0z" fill="#DC143C"/>
            <path d="M24 3C12.402 3 3 12.402 3 24c0 10.5 21 29 21 29s21-18.5 21-29C45 12.402 35.598 3 24 3z" fill="#FF0000"/>
            <path d="M24 6C14.059 6 6 14.059 6 24c0 8.25 18 24 18 24s18-15.75 18-24C42 14.059 33.941 6 24 6z" fill="#FF4444"/>
            <circle cx="24" cy="24" r="8" fill="#FFFFFF"/>
            <circle cx="24" cy="24" r="5" fill="#DC143C"/>
          </svg>
        `;
        
        const companyIcon: google.maps.Icon = {
          url: 'data:image/svg+xml;base64,' + btoa(companyMarkerSvg),
          scaledSize: new window.google.maps.Size(48, 60),
          anchor: new window.google.maps.Point(24, 60),
        };

        const companyMarker = new window.google.maps.Marker({
          position: coords,
          map: map,
          title: 'Company Location',
          icon: companyIcon,
          zIndex: 100, // Lowest z-index so workers appear above it
        });

        // Add InfoWindow for company location
        const companyInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="min-width: 200px; padding: 8px;">
              <strong style="color: #DC143C; font-size: 16px;">🏢 Company Location</strong><br/>
              <small>${companyAddress}</small>
            </div>
          `,
        });

        companyMarker.addListener('click', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            infoWindowRef.current = null;
          }
          companyInfoWindow.open({
            anchor: companyMarker,
            map: map,
          });
        });

        companyMarker.addListener('mouseover', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.close();
            infoWindowRef.current = null;
          }
          companyInfoWindow.open({
            anchor: companyMarker,
            map: map,
          });
        });

        companyMarker.addListener('mouseout', () => {
          companyInfoWindow.close();
        });

        markersRef.current.set('company-location', companyMarker);
      }
    });

    // Fit bounds to show all markers
    if (markers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach((m) => bounds.extend(m.position));
      map.fitBounds(bounds);
      
      // Include company location in bounds if available (after initial fit)
      geocodeCompanyAddress().then((coords) => {
        if (coords) {
          const updatedBounds = new window.google.maps.LatLngBounds();
          markers.forEach((m) => updatedBounds.extend(m.position));
          updatedBounds.extend(coords);
          map.fitBounds(updatedBounds);
        }
      });
    }

    // Add job locations (geocode addresses) asynchronously
    if (showJobs && Array.isArray(jobs)) {
      const jobsToShow = jobs.filter((job: any) => {
        // Filter by selected job statuses
        if (selectedJobStatuses.length > 0) {
          const jobStatus = job.status || '';
          if (!selectedJobStatuses.includes(jobStatus)) {
            return false;
          }
        }
        return true;
      });

      const jobMarkers: MapMarker[] = [];
      
      Promise.all(
        jobsToShow.map(async (job: any) => {
          const coords = await geocodeJob(job);
          if (coords) {
            const jobNumber = job.job_number || job.jobNumber || job.job;
            const jobAddress = job.job_address || job.jobAddress;
            const jobMarker: MapMarker = {
              id: `job-${job.id}`,
              type: 'job',
              position: coords,
              title: jobNumber || `Job ${job.id}`,
              description: jobAddress || 'No address',
              data: job,
            };
            jobMarkers.push(jobMarker);
            
            const jobStatus = job.status || 'Estimate';
            const customIcon = getJobStatusIcon(jobStatus);
            
            const marker = new window.google.maps.Marker({
              position: coords,
              map: map,
              title: jobMarker.title,
              icon: customIcon,
              zIndex: 1500, // Jobs above company but below workers
            });

            const showJobInfoWindow = () => {
              // Close any existing InfoWindow
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
              }
              
              // Create and open new InfoWindow
              if (map && window.google?.maps) {
                const infoWindow = new window.google.maps.InfoWindow({
                  content: `
                    <div style="min-width: 200px; padding: 8px;">
                      <strong>${jobMarker.title}</strong><br/>
                      <small>${jobMarker.description}</small>
                    </div>
                  `,
                });
                
                infoWindow.open({
                  anchor: marker,
                  map: map,
                });
                
                infoWindowRef.current = infoWindow;
              }
              
              setSelectedMarker(jobMarker);
            };

            // Show InfoWindow on hover
            marker.addListener('mouseover', showJobInfoWindow);
            
            // Open job detail modal on click
            marker.addListener('click', () => {
              // Close InfoWindow
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
              }
              // Open job detail modal
              setSelectedJobId(job.id);
              setJobModalOpen(true);
            });
            
            // Close InfoWindow on mouseout
            marker.addListener('mouseout', () => {
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
              }
              setSelectedMarker(null);
            });

            markersRef.current.set(jobMarker.id, marker);
          }
        })
      ).then(() => {
        // Fit bounds again after all job markers are added
        if (jobMarkers.length > 0 || markers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds();
          [...markers, ...jobMarkers].forEach((m) => bounds.extend(m.position));
          map.fitBounds(bounds);
        }
      }).catch((error) => {
        console.error('Error geocoding jobs:', error);
      });
    }
  }, [isLoaded, currentPositions, jobs, users, showWorkers, showJobs, selectedJobStatuses, loading]);

  // Ensure only one InfoWindow is open at a time
  useEffect(() => {
    if (!selectedMarker && infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
  }, [selectedMarker]);

  // Cleanup InfoWindow on unmount
  useEffect(() => {
    return () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
    };
  }, []);

  // Get users filtered by status
  const getUsersByStatus = useCallback((status: 'active' | 'inactive' | 'absent'): Array<{ user: User; position: CurrentPosition | null; status: 'active' | 'inactive' | 'absent' }> => {
    const approvedUsers = Array.isArray(users) ? users.filter((u) => u.approved) : [];
    
    return approvedUsers
      .map((user) => {
        const position = Array.isArray(currentPositions) 
          ? (currentPositions.find((pos) => pos.userId === user.id) || null)
          : null;
        
        const userStatus = getWorkerStatus(position?.updatedAt || null);
        
        return {
          user,
          position,
          status: userStatus || 'absent',
        };
      })
      .filter((item) => item.status === status);
  }, [users, currentPositions, getWorkerStatus]);

  // Calculate worker status counts - memoized to recalculate when users or currentPositions change
  // Only calculate when data is ready to avoid showing 0 counts on initial render
  const workerStatusCounts = useMemo(() => {
    // Don't calculate if data isn't ready yet
    if (!dataReady) {
      return { activeCount: 0, inactiveCount: 0, absentCount: 0 };
    }
    
    // Calculate counts for approved users only
    // Use updatedAt from currentPositions for status calculation
    const approvedUsers = Array.isArray(users) ? users.filter((u) => u.approved) : [];
    let activeCount = 0;
    let inactiveCount = 0;
    let absentCount = 0;
    
    approvedUsers.forEach((user) => {
      // Find the user's current position
      const position = Array.isArray(currentPositions) 
        ? currentPositions.find((pos) => pos.userId === user.id)
        : null;
      
      // Use updatedAt from currentPositions if available, otherwise null (absent)
      const status = getWorkerStatus(position?.updatedAt || null);
      if (status === 'active') {
        activeCount++;
      } else if (status === 'inactive') {
        inactiveCount++;
      } else if (status === 'absent') {
        absentCount++;
      }
    });
    
    return { activeCount, inactiveCount, absentCount };
  }, [users, currentPositions, getWorkerStatus, dataReady]);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const onUnmount = () => {
    // Close InfoWindow on unmount
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
    mapRef.current = null;
  };

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading Google Maps. Please check your API key configuration.
        </Alert>
      </Box>
    );
  }

  if (!apiKey) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Google Maps API key not configured. Please set VITE_GOOGLE_MAPS_API_KEY in your .env file.
        </Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Map
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor crew locations and jobs
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Filters
          </Typography>
          
          <FormControlLabel
            control={
              <Checkbox
                checked={showWorkers}
                onChange={(e) => setShowWorkers(e.target.checked)}
              />
            }
            label="Show Workers"
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={showJobs}
                onChange={(e) => setShowJobs(e.target.checked)}
              />
            }
            label="Show Jobs"
          />

          {showJobs && (
            <Autocomplete
              multiple
              options={['Estimate', 'Proposal', 'Measure', 'Shop Drawing', 'Fabrication', 'Install', 'Finish']}
              value={selectedJobStatuses}
              onChange={(_, newValue) => {
                setSelectedJobStatuses(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Filter by Job Status" size="small" sx={{ mt: 1 }} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                  />
                ))
              }
              sx={{ mt: 1 }}
            />
          )}
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Legend
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon 
                sx={{ 
                  color: '#4285F4',
                  fontSize: '1.2rem',
                }} 
              />
              <Typography variant="body2">Workers</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Worker Status:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5, flexWrap: 'wrap' }}>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={() => {
                    setSelectedStatus('active');
                    setStatusListModalOpen(true);
                  }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Active: {workerStatusCounts.activeCount}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={() => {
                    setSelectedStatus('inactive');
                    setStatusListModalOpen(true);
                  }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#FF9800' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Inactive: {workerStatusCounts.inactiveCount}
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7 }
                  }}
                  onClick={() => {
                    setSelectedStatus('absent');
                    setStatusListModalOpen(true);
                  }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#9E9E9E' }} />
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    Absent: {workerStatusCounts.absentCount}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
                Job Status:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {['Estimate', 'Proposal', 'Measure', 'Shop Drawing', 'Fabrication', 'Install', 'Finish'].map((status) => {
                  const config = jobStatusIconConfig[status];
                  const IconComponent = config?.IconComponent || DescriptionIcon;
                  return (
                    <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconComponent 
                        sx={{ 
                          color: config?.color || '#FF9800',
                          fontSize: '1.2rem',
                        }} 
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {status}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Box>

      {dataReady && (
        <Box
          sx={{
            flex: 1,
            minHeight: 500,
            height: 'calc(100vh - 300px)',
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={10}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: true,
            fullscreenControl: true,
          }}
        >
        </GoogleMap>
        </Box>
      )}

      {/* Job Detail Modal */}
      <Dialog
        open={jobModalOpen}
        onClose={() => setJobModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogContent 
          sx={{ 
            p: 0, 
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            },
          }}
        >
          {selectedJobId && (
            <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
              <JobDetail
                jobId={selectedJobId}
                onBack={() => setJobModalOpen(false)}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Worker Status List Modal */}
      <Dialog
        open={statusListModalOpen}
        onClose={() => {
          setStatusListModalOpen(false);
          setSelectedStatus(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedStatus === 'active' && 'Active Workers'}
            {selectedStatus === 'inactive' && 'Inactive Workers'}
            {selectedStatus === 'absent' && 'Absent Workers'}
          </Typography>
          <IconButton
            onClick={() => {
              setStatusListModalOpen(false);
              setSelectedStatus(null);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedStatus && (
            <List>
              {getUsersByStatus(selectedStatus).length === 0 ? (
                <ListItem>
                  <ListItemText 
                    primary="No workers found" 
                    secondary={`No workers with ${selectedStatus} status`}
                  />
                </ListItem>
              ) : (
                getUsersByStatus(selectedStatus).map((item) => {
                  const statusColor = 
                    item.status === 'active' ? '#4CAF50' :
                    item.status === 'inactive' ? '#FF9800' : '#9E9E9E';
                  const statusText = 
                    item.status === 'active' ? 'Active' :
                    item.status === 'inactive' ? 'Inactive' : 'Absent';
                  
                  const lastUpdate = item.position?.updatedAt 
                    ? new Date(item.position.updatedAt).toLocaleString()
                    : 'No position data';
                  
                  return (
                    <ListItem key={item.user.id} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {item.user.username}
                            </Typography>
                            <Chip
                              label={statusText}
                              size="small"
                              sx={{
                                backgroundColor: statusColor,
                                color: 'white',
                                fontSize: '0.7rem',
                                height: '20px',
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Email: {item.user.email}
                            </Typography>
                            {item.user.phone && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Phone: {item.user.phone}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Last update: {lastUpdate}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  );
                })
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setStatusListModalOpen(false);
            setSelectedStatus(null);
          }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
